import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    FormProvider,
    useFieldArray,
    useForm,
    UseFormMethods,
    ValidateResult
} from '../../../Vendor/react-hook-form/dist/index.ie11'
import { SelfProvisioning as SelfProvisioningLanguage, UserDetailsLanguage } from '../../../../i18n'
import { DEVICE_FILTER_EXTENSIONS_ONLY } from '../../../NonReact/constants/DeviceConstants'
import { EMAIL_REGEX } from '../../../NonReact/constants/Regexes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { SERVER_NONE_VALUE } from '../../AddUser/Constants'
import Checkbox from '../../Common/Components/Checkbox'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import { FreeTextSelect } from '../../Common/Components/Selects/FreeTextSelect/FreeTextSelect'
import { ComboBoxItem } from '../../Common/Components/Selects/Types'
import TextInput from '../../Common/Components/TextInput'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { displayError } from '../../Common/Utils/Messages'
import { FooterActions } from '../Actions/FooterActions'
import { UserTypes } from '../Constants/Constants'
import {
    USER_ARRAY_NAME_FIELD_NAME,
    USER_ARRAY_WELCOME_EMAIL_FIELD_NAME,
    USER_EMAIL_FIELD_NAME,
    USER_TYPE_FIELD_NAME,
    userArrayFieldName,
    USERS_ARRAY_FIELD_NAME
} from '../Constants/FieldNames'
import { SelfProvisioningConfig, SelfProvisioningConfigContext } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContext, SelfProvisioningContextType } from '../Contexts/SelfProvisioningContext'
import { userFormStateMapper } from '../Mappers/UserMapper'
import { createUser, getDevices } from '../../../NonReact/common/API/EntityAPIClient'
import {
    SelfProvisioningUser,
    SelfProvisioningUserFormState
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { AdminUserBody } from '../../../NonReact/common/Types/Types'

type Props = {
    sessionNonce?: string
}

type UserResponseState = {
    index: number
    isError?: boolean
    message?: string
}

export const UserEntry: FC<Props> = ({ sessionNonce }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(UserDetailsLanguage.NAME)

    const config: SelfProvisioningConfig = useContext(SelfProvisioningConfigContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const currentUser: UserInfoContextType = useContext(UserInfoContext)
    const {
        telSysState: [telSysID],
        partitionState: [partitionID],
        usersState: [, setUsers],
        nextStep
    }: SelfProvisioningContextType = useContext(SelfProvisioningContext)

    const [availableDevices, setAvailableDevices]: ReactState<Nullable<DevicesResponse[]>> =
        useState<Nullable<DevicesResponse[]>>()
    const [availableDevicesOptions, setAvailableDevicesOptions]: ReactState<Nullable<ComboBoxItem<string>[]>> =
        useState<Nullable<ComboBoxItem<string>[]>>()

    const defaultUser: SelfProvisioningUser = {
        userType: UserTypes.REPORTING_SUPERVISOR,
        email: '',
        name: '',
        welcomeEmail: false
    }

    const methods: UseFormMethods<SelfProvisioningUserFormState> = useForm({
        defaultValues: {
            users: [defaultUser]
        },
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE
    })

    const {
        setValue,
        handleSubmit,
        control,
        watch,
        formState: { isValid }
    }: UseFormMethods<SelfProvisioningUserFormState> = methods

    const { fields, append, remove } = useFieldArray({
        control,
        name: USERS_ARRAY_FIELD_NAME
    })

    const [userResponses, setUserResponses]: ReactState<UserResponseState[]> = useState<UserResponseState[]>([])

    const usersArrayFieldValue: SelfProvisioningUser[] = watch(USERS_ARRAY_FIELD_NAME)
    // There is a required minimum of one reporting supervisor user.
    const hasReportingSupervisor: boolean = usersArrayFieldValue.some(
        ({ userType }: SelfProvisioningUser): boolean => userType === UserTypes.REPORTING_SUPERVISOR
    )

    // What to do when the form is submitted.
    const submitUserData = (state: SelfProvisioningUserFormState): void => {
        // Make sure the form is valid when submitted, otherwise do nothing.
        if (isValid && state.users.length > 0 && telSysID && partitionID && hasReportingSupervisor) {
            // Make the requests to create all of the users.
            const userPromises: Promise<UserResponseState>[] = state.users.map(
                (user: SelfProvisioningUser, index: number): Promise<UserResponseState> => {
                    if (userResponses.length < index || userResponses[index]?.isError !== false) {
                        // Is the name entered one of the devices in the list?
                        const device: Nullable<DevicesResponse> = availableDevices?.find(
                            ({ DeviceNumber }: DevicesResponse): boolean => DeviceNumber === user.name
                        )

                        // Create a user.
                        return userFormStateMapper(
                            config?.userDefaults,
                            { ...user, useEmailAsUsername: true },
                            currentUser?.Scope.CMEnvironmentID ?? '',
                            telSysID,
                            partitionID,
                            sessionNonce,
                            device
                        ).then(
                            (userBody: AdminUserBody): Promise<UserResponseState> =>
                                createUser(userBody)
                                    .then((): UserResponseState => ({ index, isError: false }))
                                    .catch(
                                        (error: Error): UserResponseState => ({
                                            index,
                                            isError: true,
                                            message: error.message
                                        })
                                    )
                        )
                    }

                    // If we have already created this user then resolve immediately.
                    return Promise.resolve({ index, isError: false })
                }
            )

            // Wait for all of the user promises to resolve.
            Promise.all(userPromises).then((results: UserResponseState[]): void => {
                if (results.every((result: UserResponseState): boolean => !result.isError)) {
                    setUsers(
                        state.users.map(
                            (user: SelfProvisioningUser): SelfProvisioningUser => ({
                                ...user,
                                name:
                                    availableDevices?.find(
                                        ({ DeviceNumber }: DevicesResponse): boolean => DeviceNumber === user.name
                                    )?.Description ?? user.name
                            })
                        )
                    )

                    nextStep()
                }

                if (isMounted.current) {
                    setUserResponses(
                        // Make sure all of the promises stay in the same order.
                        results.sort(
                            ({ index: indexA }: UserResponseState, { index: indexB }: UserResponseState): number =>
                                indexA - indexB
                        )
                    )
                }
            })
        }

        return undefined
    }

    const userTypeOptions: SelectOptions<string> = [
        {
            label: language.get(SelfProvisioningLanguage.SelfProvisioning_Users_UserType_ReportingSupervisor),
            value: UserTypes.REPORTING_SUPERVISOR
        },
        {
            label: language.get(SelfProvisioningLanguage.SelfProvisioning_Users_UserType_OneUser),
            value: UserTypes.ONE_USER
        }
    ]

    useEffect((): void => {
        if (telSysID && partitionID) {
            // Get the list of devices.
            getDevices(telSysID, partitionID, false, DEVICE_FILTER_EXTENSIONS_ONLY)
                .then((devices: DevicesResponse[]): void => {
                    if (isMounted.current) {
                        setAvailableDevices(devices)
                        setAvailableDevicesOptions(
                            devices.map(
                                ({ Description, DeviceNumber }: DevicesResponse): ComboBoxItem<string> => ({
                                    label: `${DeviceNumber} - ${Description}`,
                                    value: DeviceNumber
                                })
                            )
                        )
                    }
                })
                .catch(displayError)
        }
    }, [])

    return (
        <FormProvider {...methods}>
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <form noValidate onSubmit={handleSubmit(submitUserData)}>
                <div className='user-entry'>
                    <h2 className='user-entry-page-title'>
                        {language.get(SelfProvisioningLanguage.SelfProvisioning_Users_Title)}
                    </h2>
                    <div className='user-entry-header'>
                        <div className='spacer'></div>
                        <div className='user-type-entry'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_Users_UserType_Label)}
                        </div>
                        <div className='email-entry'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_Users_Email_Label)}
                        </div>
                        <div className='name-entry'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_Users_Name_Label)}
                        </div>
                        <div className='welcome-entry'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_Users_WelcomeEmail_Label)}
                        </div>
                    </div>
                    <div className='user-entry-row-container'>
                        {fields?.map((item, index): ReactElement => {
                            const userResponse: Nullable<UserResponseState> = userResponses[index]
                            const userSuccess: boolean = userResponse?.isError === false

                            const userTypeFieldName: string = userArrayFieldName(USER_TYPE_FIELD_NAME, index)
                            const emailFieldName: string = userArrayFieldName(USER_EMAIL_FIELD_NAME, index)
                            const nameFieldName: string = userArrayFieldName(USER_ARRAY_NAME_FIELD_NAME, index)
                            const welcomeEmailFieldName: string = userArrayFieldName(
                                USER_ARRAY_WELCOME_EMAIL_FIELD_NAME,
                                index
                            )

                            return (
                                <React.Fragment key={`${item.id}-user`}>
                                    <div className='user-entry-row'>
                                        {userSuccess ? (
                                            <Icon className='success-icon' iconName={IconName.SUCCESS} />
                                        ) : (
                                            <IconButton
                                                iconName={IconName.REMOVE}
                                                type={IconButtonType.BUTTON}
                                                onClick={(): void => remove(index)}
                                            />
                                        )}
                                        <div className='user-type-entry'>
                                            <ValidationErrors name={userTypeFieldName}>
                                                <Select
                                                    label={language.get(
                                                        SelfProvisioningLanguage.SelfProvisioning_Users_UserType_Label
                                                    )}
                                                    name={userTypeFieldName}
                                                    options={userTypeOptions}
                                                    key={`${item.id}-UserType`}
                                                    renderSelectedValue={false}
                                                    disabled={userSuccess}
                                                    rules={{ required: true }}
                                                    handleChange={(): void => setValue(nameFieldName, '')}
                                                />
                                            </ValidationErrors>
                                        </div>
                                        <div className='email-entry'>
                                            <ValidationErrors name={emailFieldName}>
                                                <TextInput
                                                    labelLeft={language.get(
                                                        SelfProvisioningLanguage.SelfProvisioning_Users_Email_Label
                                                    )}
                                                    inputName={emailFieldName}
                                                    disabled={userSuccess}
                                                    rules={{
                                                        required: language.get(
                                                            SelfProvisioningLanguage.SelfProvisioning_Users_Validation_Email
                                                        ),
                                                        validate: {
                                                            isEmailAddress: (email: string): ValidateResult => {
                                                                if (
                                                                    !IsStringEmpty(email) &&
                                                                    !email?.match(EMAIL_REGEX)
                                                                ) {
                                                                    return language.get(
                                                                        UserDetailsLanguage.EmailPatternValidation
                                                                    )
                                                                }

                                                                return undefined
                                                            }
                                                        }
                                                    }}
                                                />
                                            </ValidationErrors>
                                        </div>
                                        {watch(userTypeFieldName) === UserTypes.ONE_USER ? (
                                            <div className='name-entry'>
                                                {availableDevicesOptions && (
                                                    <ValidationErrors name={nameFieldName}>
                                                        <ComboBox
                                                            label={language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_Users_Name_Label
                                                            )}
                                                            defaultOption={{
                                                                label: language.get(
                                                                    SelfProvisioningLanguage.SelfProvisioning_Users_Devices_None
                                                                ),
                                                                value: SERVER_NONE_VALUE
                                                            }}
                                                            name={nameFieldName}
                                                            options={availableDevicesOptions}
                                                            disabled={userSuccess}
                                                            rules={{
                                                                required: language.get(
                                                                    SelfProvisioningLanguage.SelfProvisioning_Users_Validation_Name
                                                                ),
                                                                validate: {
                                                                    noNone: (deviceNumber: string): ValidateResult => {
                                                                        if (deviceNumber === SERVER_NONE_VALUE) {
                                                                            return language.get(
                                                                                SelfProvisioningLanguage.SelfProvisioning_Users_Validation_DeviceNumber
                                                                            )
                                                                        }

                                                                        return undefined
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </ValidationErrors>
                                                )}
                                            </div>
                                        ) : (
                                            <div className='name-entry'>
                                                <ValidationErrors name={nameFieldName}>
                                                    <FreeTextSelect
                                                        label={language.get(
                                                            SelfProvisioningLanguage.SelfProvisioning_Users_Name_Label
                                                        )}
                                                        name={nameFieldName}
                                                        disabled={userSuccess}
                                                        options={availableDevicesOptions ?? []}
                                                        rules={{
                                                            maxLength: {
                                                                value: 100,
                                                                message: language.get(
                                                                    UserDetailsLanguage.NameValidation
                                                                )
                                                            },
                                                            required: language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_Users_Validation_Name
                                                            )
                                                        }}
                                                    />
                                                </ValidationErrors>
                                            </div>
                                        )}
                                        <div className='welcome-entry'>
                                            <Checkbox
                                                labelLeft={language.get(
                                                    SelfProvisioningLanguage.SelfProvisioning_Users_WelcomeEmail_Label
                                                )}
                                                name={welcomeEmailFieldName}
                                                disabled={userSuccess}
                                            />
                                        </div>
                                    </div>
                                    {userResponse?.isError ? (
                                        <div className='user-error'>{userResponse.message}</div>
                                    ) : (
                                        <></>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </div>
                    <div className='user-adder-container'>
                        <IconButton
                            customClassName='user-adder'
                            iconName={IconName.ADD}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => append(defaultUser)}
                        />
                    </div>
                </div>

                <FooterActions
                    showRightSideButton
                    rightSideButtonLabel={language.get(SelfProvisioningLanguage.SelfProvisioning_FooterActions_Next)}
                    rightSideButtonType='submit'
                    rightButtonDisabled={!hasReportingSupervisor}
                />
            </form>
        </FormProvider>
    )
}
